import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const PARTICLE_COUNT = 20;

const HeroSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [isVideoHidden, setIsVideoHidden] = useState(false);
  const gestureRetryRef = useRef(null);
  const videoElRef = useRef(null);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)')?.matches;
    setPrefersReducedMotion(prefersReduced);
    if (prefersReduced) return undefined;
    const handleScroll = () => {};
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Particle positions only need to be computed once per mount, not on every render.
  const particles = useMemo(() => (
    [...Array(PARTICLE_COUNT)]?.map(() => ({
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
      animationDelay: `${Math.random() * 3}s`,
      animationDuration: `${3 + Math.random() * 2}s`
    }))
  ), []);

  const attemptPlay = useCallback((video) => {
    if (!video) return;
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise
        .then(() => setIsVideoHidden(false))
        .catch(() => {
          // Autoplay blocked (e.g. Low Power Mode) — hide the video (via CSS, not
          // unmount) so the native centered play glyph never sits over the
          // headline, while keeping the element + ref alive for the gesture retry.
          setIsVideoHidden(true);

          if (!gestureRetryRef.current) {
            const retry = () => {
              const el = videoElRef.current;
              if (!el) return;
              el.muted = true;
              el.play()
                .then(() => setIsVideoHidden(false))
                .catch(() => {});
            };
            gestureRetryRef.current = retry;
            window.addEventListener('touchstart', retry, { once: true, passive: true });
            window.addEventListener('click', retry, { once: true });
          }
        });
    }
  }, []);

  // Remove the dangling gesture-retry listeners if the component unmounts before
  // the user ever taps/clicks.
  useEffect(() => {
    return () => {
      if (gestureRetryRef.current) {
        window.removeEventListener('touchstart', gestureRetryRef.current);
        window.removeEventListener('click', gestureRetryRef.current);
      }
    };
  }, []);

  // Ensure `muted` is set as a real DOM property (React doesn't reliably emit the
  // attribute) before attempting to play, otherwise Safari rejects autoplay. The
  // video stays mounted for the lifetime of the component (hidden via CSS only)
  // so this ref callback fires once, keeping videoElRef valid for gesture retries.
  const setVideoRef = useCallback((el) => {
    videoElRef.current = el;
    if (!el) return;
    el.muted = true;
    attemptPlay(el);
  }, [attemptPlay]);

  const handleOpenApp = () => {
    window.open('https://depthme.app', '_blank', 'noopener,noreferrer');
  };

  return (
    <section id="hero" className="relative min-h-dvh flex items-center justify-center overflow-hidden">
      {/* Background Layer 1: Video — static, full presence. Kept mounted even when
          autoplay fails (hidden via CSS instead) so the ref stays valid for the
          one-time gesture retry below; the native play glyph is still fully
          suppressed since the element is invisible and non-interactive. */}
      <div className="absolute inset-0 pointer-events-none">
        <video
          ref={setVideoRef}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster="/hero-poster.jpg"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
            isVideoHidden ? 'opacity-0 invisible' : 'opacity-55'
          }`}
        >
          <source src="/hero-bg.mp4" type="video/mp4" />
        </video>
      </div>

      {/* Background Layer 2: Light gradient overlay (strengthened through the middle
          so the headline stays legible even when the video is hidden/unplayed). */}
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-background/40 via-background/50 to-background/70" />

      {/* Background Layer 3: Cosmic gradient tint */}
      <div className="absolute inset-0 cosmic-gradient pointer-events-none" style={{ opacity: 0.35 }}>
        {!prefersReducedMotion && (
          <>
            <div className="absolute inset-0 pointer-events-none">
              {particles?.map((particle, i) => (
                <div
                  key={i}
                  className="absolute w-1 h-1 bg-primary/30 rounded-full animate-pulse"
                  style={particle}
                />
              ))}
            </div>
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-accent/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
          </>
        )}
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
        {/* Logo */}
        <div className={`mb-8 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="relative inline-block">
            <div className="absolute inset-0 rounded-full bg-primary/20 blur-xl scale-150 animate-pulse" />
            <Image
              src="/brand/depthme-icon-192.png"
              alt="DepthMe luminous sphere logo representing AI-guided journaling for self-discovery"
              className="relative w-24 h-24 mx-auto object-contain drop-shadow-2xl"
            />
          </div>
        </div>

        {/* Top Label */}
        <div className={`mb-6 transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-block px-4 py-2 glassmorphism rounded-full text-sm font-medium text-muted-foreground">
            Mind reframing · Your voice · Daily rituals
          </span>
        </div>

        {/* Main Headline */}
        <div className={`space-y-6 mb-12 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-foreground leading-tight">
            Your inner voice,{' '}
            <span className="text-gradient">upgraded.</span>
          </h1>

          <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            DepthMe replaces the thoughts holding you back — through mind reframing, your own voice, and daily rituals built around your real situation.
          </p>
        </div>

        {/* Primary CTA: Open the App */}
        <div className={`mb-8 flex justify-center transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <Button
            onClick={handleOpenApp}
            variant="default"
            size="lg"
            className="violet-glow text-lg py-4 px-12"
            iconName="ArrowRight"
            iconPosition="right"
          >
            Open the App
          </Button>
        </div>

        {/* Trust Line */}
        <div className={`transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="text-muted-foreground">
            If you don't train your mind, your mind will train you.
          </p>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
